<template>
    <BaseTemplate :page-title="''" :app-name="'CCDTI'">
      <template #content>
        <div class="p-4 space-y-6">
          <!-- Welcome Section -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <div class="flex items-center justify-between gap-4">
              <div class="flex items-center space-x-4">
                <div class="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                  <Home class="h-6 w-6 text-blue-600 dark:text-blue-300" />
                </div>
                <div>
                  <h2 class="text-2xl font-bold text-gray-800 dark:text-white">Bem-vindo(a), {{ firstName }}!</h2>
                  <p class="text-gray-500 dark:text-gray-400">{{ getCurrentDateMessage() }}</p>
                </div>
              </div>
              <div class="hidden sm:block">
                <img
                  :src="zegotinhaImage"
                  alt="Zé Gotinha"
                  class="h-24 w-auto object-contain zegotinha-float"
                />
              </div>
            </div>
          </div>
  
          <!-- Modules Grid -->
          <div class="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div v-for="(module, index) in modules" :key="index" 
                 @click="navigateTo(module.route)"
                 class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer">
              <div class="flex flex-col items-center text-center space-y-4">
                <div class="p-4 rounded-full" :class="module.bgColor">
                  <component :is="module.icon" class="h-8 w-8" :class="module.iconColor" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-800 dark:text-white">{{ module.title }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ module.description }}</p>
                </div>
                <div class="mt-2 text-blue-600 dark:text-blue-400 text-sm font-medium flex items-center">
                  <span>Acessar</span>
                  <ArrowRight class="h-4 w-4 ml-1" />
                </div>
              </div>
            </div>
          </div>
  

    <!-- Modules Carousel for Mobile -->
    <div class="md:hidden px-4 py-4">
    <div class="grid grid-cols-2 gap-4">
        <div v-for="(module, index) in modules" :key="index"
            @click="navigateTo(module.route)"
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 flex flex-col items-center justify-between h-36 transition-transform hover:scale-105 cursor-pointer">
        <div class="p-3 rounded-full mb-2" :class="module.bgColor">
            <component :is="module.icon" class="h-6 w-6" :class="module.iconColor" />
        </div>
        <div class="text-center">
            <h3 class="text-sm font-semibold text-gray-800 dark:text-white leading-tight">{{ module.title }}</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ module.description }}</p>
        </div>
        </div>
    </div>
    </div>

          <!-- Quick Stats -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div v-for="(stat, index) in stats" :key="index" 
                 class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border-l-4" 
                 :class="stat.borderColor">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ stat.title }}</p>
                  <h3 class="text-2xl font-bold text-gray-800 dark:text-white mt-1">{{ stat.value }}</h3>
                </div>
                <div class="p-3 rounded-full" :class="stat.bgColor">
                  <component :is="stat.icon" class="h-6 w-6" :class="stat.iconColor" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </BaseTemplate>


  </template>
  
  <script setup>
  import { onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { 
    Home, 
    Shield, 
    TrendingUp,
    Activity, 
    FileText,
    Settings,
    Database,
    Monitor,
    ArrowRight,
    FileWarning,
    Handshake,
    Cloud,
  //  ClipboardCheck,
    Scale
  } from 'lucide-vue-next';
  
import BaseTemplate from '@/views/templates/BaseTemplate.vue'; // Importando o template base
import { useUserStore } from '@/stores/userStore';
import zegotinhaImageSrc from '@/assets/zegotinha.png';
import { getUser } from '@/services/authService';

import 'swiper/swiper-bundle.css';
  
const router = useRouter();
  
const userStore = useUserStore();
const zegotinhaImage = zegotinhaImageSrc;
const firstName = computed(() => userStore.nome || 'Usuário');
const userGroups = computed(() => userStore.grupos || []);
const userAppPermissions = computed(() => userStore.appPermissions || {});

  
  // Função para obter mensagem baseada na hora do dia
  const getCurrentDateMessage = () => {
    const now = new Date();
    const hour = now.getHours();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = now.toLocaleDateString('pt-BR', options);
    
    let greeting = '';
    if (hour < 12) greeting = 'Bom dia';
    else if (hour < 18) greeting = 'Boa tarde';
    else greeting = 'Boa noite';
    
    return `${greeting}! Hoje é ${dateStr}`;
  };
  
  
  const allModules = [
  {
    title: 'Eventos',
    description: 'Gerenciar eventos e ocorrências',
    icon: FileText,
    route: '/eventos',
    bgColor: 'bg-violet-100 dark:bg-violet-900',
    iconColor: 'text-violet-600 dark:text-violet-300',
    visibleFor: ['superadmin', 'engenhariaclinica'],
  },
  {
    title: 'Feed',
    description: 'Visualizar Postagens',
    icon: Cloud,
    route: '/feed',
    appKey: 'feed',
    bgColor: 'bg-teal-100 dark:bg-teal-900',
    iconColor: 'text-teal-600 dark:text-teal-300',
  },
  {
    title: 'Dashboard Equipamentos',
    description: 'Monitorar equipamentos',
    icon: Monitor,
    route: '/dashboardequipamentos',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900',
    iconColor: 'text-emerald-600 dark:text-emerald-300',
    visibleFor: ['admin', 'superadmin', 'desenvolvedor', 'manager', 'ti', 'direcao', 'engenhariaclinica', 'user'],
  },
  
 // {
 //   title: 'Check-In',
  //  description: 'Gerenciar Eventos (Reuniões)',
  //  icon: ClipboardCheck,
 //   route: '/checkin',
  //  bgColor: 'bg-orange-100 dark:bg-orange-900',
  //  iconColor: 'text-orange-600 dark:text-orange-300',
  //  visibleFor: ['admin', 'superadmin', 'desenvolvedor', 'ti', 'direcao', 'checkin'],
 // },
    {
    title: 'Eventos Adversos',
    description: 'Gerenciar Eventos Adversos',
    icon: FileWarning,
    route: '/eventosadversos',
    appKey: 'eventos-adversos',
    bgColor: 'bg-pink-100 dark:bg-pink-900',
    iconColor: 'text-pink-600 dark:text-pink-300',
  },
  {
    title: 'Instâncias',
    description: 'Gerenciar instâncias do sistema',
    icon: Activity,
    route: '/dashboardinstancias',
    bgColor: 'bg-sky-100 dark:bg-sky-900',
    iconColor: 'text-sky-600 dark:text-sky-300',
    visibleFor: ['superadmin', 'ti'],
  },
  {
    title: 'Guardian',
    description: 'Monitorar servidores',
    icon: Shield,
    route: '/guardian',
    appKey: 'guardian',
    bgColor: 'bg-orange-100 dark:bg-orange-900',
    iconColor: 'text-orange-600 dark:text-orange-300',
    visibleFor: ['superadmin', 'ti'],
  },
  {
    title: 'Indicadores',
    description: 'Visualizar indicadores de desempenho',
    icon: TrendingUp,
    route: '/indicadores',
    appKey: 'indicadores',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900',
    iconColor: 'text-indigo-600 dark:text-indigo-300',
    visibleFor: ['superadmin', 'ti', 'indicadores'],
  },
  {
    title: 'SQLs Diversos',
    description: 'Visualizar SQLs diversos do InovaGuardian',
    icon: Database,
    route: '/diversos',
    appKey: 'diversos',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
    iconColor: 'text-slate-700 dark:text-slate-200',
    visibleFor: ['superadmin', 'ti', 'indicadores'],
  },
  {
    title: 'Demandas Judiciais',
    description: 'Gerenciar demandas judiciais',
    icon: Scale,
    route: '/modulo-juridico',
    appKey: 'juridico',
    bgColor: 'bg-pink-100 dark:bg-pink-900',
    iconColor: 'text-pink-600 dark:text-pink-300',
    visibleFor: ['juridico', 'juridico_admin', 'superadmin'],
  },
  {
    title: 'Ativos',
    description: 'Gerenciar ativos de TI',
    icon: Database,
    route: '/ativos',
    bgColor: 'bg-red-100 dark:bg-red-900',
    iconColor: 'text-red-600 dark:text-red-300',
    visibleFor: ['superadmin', 'ti'],
  },
  {
    title: 'Pendências',
    description: 'Visualizar tarefas pendentes',
    icon: FileText,
    route: '/pendencias',
    bgColor: 'bg-amber-100 dark:bg-amber-900',
    iconColor: 'text-amber-600 dark:text-amber-300',
    visibleFor: ['direcao', 'superadmin', 'coordenacao'],
  },
  ////{
  //  title: 'Documentos',
   // description: 'Visualizar meus documentos',
  //  icon: FolderIcon,
 //   route: '/documents',
  //  bgColor: 'bg-lime-100 dark:bg-lime-900',
  //  iconColor: 'text-lime-600 dark:text-lime-300',
  //  visibleFor: ['ti','direcao', 'superadmin', 'coordenacao'],
 // },
  {
    title: 'Controle de Folgas',
    description: 'Organize as folgas dos colaboradores',
    icon: Handshake,
    route: '/manager/daysoff',
    appKey: 'daysoff',
    bgColor: 'bg-green-100 dark:bg-green-900',
    iconColor: 'text-green-600 dark:text-green-300',
    visibleFor: ['ti','direcao', 'superadmin', 'coordenacao'],
  },
  ////{
   // title: 'Tickets',
  //  description: 'Precisa de ajuda ?',
  //  icon: Headset,
 //   route: '/help',
  //  bgColor: 'bg-fuchsia-100 dark:bg-fuchsia-900',
  //  iconColor: 'text-fuchsia-600 dark:text-fuchsia-300',
   // visibleFor: ['superadmin', 'ti', 'desenvolvedor'],
 // },
  {
    title: 'Configurações',
    description: 'Configurações do Sistema',
    icon: Settings,
    route: '/config',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900',
    iconColor: 'text-cyan-600 dark:text-cyan-300',
    visibleFor: ['superadmin', 'ti', 'direcao', 'engenhariaclinica'],
  },
];


const hasModuleAccess = (module) => {
  if (module.appKey) {
    return !!userAppPermissions.value[module.appKey];
  }
  if (!module.visibleFor || module.visibleFor.length === 0) return true;
  return module.visibleFor.some(group => userGroups.value.includes(group));
};

const modules = computed(() =>
  allModules.filter(module => hasModuleAccess(module))
);

// Função para navegação
const navigateTo = (route) => {
  router.push(route);
};

onMounted(async () => {
  try {
    if (!Object.keys(userStore.appPermissions || {}).length) {
      await getUser();
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }
  } catch (err) {
    console.error('Erro ao limpar caches da aplicação na HomePage:', err);
  }
});
  </script>
  
  
